import * as React from 'react'
import type { GhaPriceMatchClickThrough, GhaPriceMatchParams } from 'lib/metasearch/ghaPriceMatch'
import type { IntlShape } from 'react-intl'

import { validatePropertyRateBundle } from 'api/accommodation'
import type { CreateReservationEnquiryProps } from 'api/bedbank'
import { getExperiencesById, getExperienceTransferAvailability } from 'api/experiences'
import { initAccommodationV2Sessions } from 'actions/AccommodationSessionActions'
import { addToCheckoutCart, initBedbankSession, initialiseCheckout, removeItem, setCheckoutGuestMode } from 'actions/CheckoutActions'
import { createGhaVoucher } from 'actions/MetasearchVoucherActions'
import { fetchPropertyRates, updatePropertyPricingData } from 'actions/PropertyActions'
import { getSource, getSourceForProperty, ghaPricedRequest } from 'selectors/offerSelectors'
import { markPropertyRateSellingPrice } from 'lib/channelMarkup/channelMarkup'
import { addToUserCart } from 'actions/UserCartActions'
import { trackClientEvent, trackEvent } from 'analytics/analytics'
import { addPropertyRateToCartGTM } from 'analytics/eventDefinitions'
import { addToCart } from 'analytics/snowplow/events'
import { fireEcommerceGTMEvent } from 'api/googleTagManager'
import ProcessingModal from 'checkout/Components/Booking/Modals/ProcessingModal'
import { navigateToCheckout } from 'checkout/actions/navigateActions'
import { generatePassengersFromOccupants } from 'checkout/lib/utils/flights/passenger'
import { getPropertyRatePricingSellingPrice, getUpgradePricingDiscount, hasPropertyRateUpgradeFeeMismatchBetweenRates } from 'checkout/lib/utils/loyalty/propertyRateLoyaltyUpgrade'
import { recordRejectedPropertyRateUpgrade } from 'checkout/lib/utils/loyalty/rejectedPropertyRateUpgrades'
import { resolveUpgradeFailure } from 'checkout/lib/utils/loyalty/resolveUpgradeFailure'
import BedbankChangePriceModal from 'components/OfferPage/Bedbank/Modals/BedbankChangePriceModal'
import BedbankSoldOutModal from 'components/OfferPage/Bedbank/Modals/BedbankSoldOutModal'
import PropertyLoyaltyUpgradeUnavailableModal from 'components/OfferPage/Property/PropertyLoyaltyUpgradeUnavailableModal'
import { FlightViewTypes } from 'constants/flight'
import { SOURCE_TYPES } from 'constants/source'
import { EXPERIENCE_ANY_DATE } from 'constants/experience'
import type { ModalContextType } from 'contexts/ModalContext'
import type { ModalElementContextValue } from 'contexts/ModalProvider'
import type {
  OfferPageDispatchAction,
  OfferPageRoomOption,
} from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { isAccommodationV2Booking } from 'lib/accommodation/isAccommodationV2Booking'
import { getHotelOnlyPropertyRatePricing } from 'lib/accommodation/buildEnquirySessionPayload'
import {
  generateAccommodationV2CheckoutItem,
  generateBedbankPropertyAccommodationCheckoutItems,
  getAccommodationV2RateFields,
  generateLpcPropertyAccommodationCheckoutItems,
  withPropertyRateUpgradeSessions,
} from 'lib/checkout/accommodation/cart'
import { generateBundledAccommodationExperienceItem } from 'lib/checkout/experiences/cart'
import { getDistanceBetweenPropertyAndAirport } from 'components/Experiences/ExperienceTransfers/ExperienceTransferUtils'
import { generateFlightCheckoutItem } from 'lib/checkout/flights/cart'
import { floatify } from 'lib/maths/mathUtils'
import { countOccupants, countOccupantsForFlights, sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { isBedbankRate, isLpcRate } from 'lib/offer/propertyTypes'
import { setPendingGhaVoucher } from 'lib/metasearch/pendingGhaVoucher'
import { reportClientError } from 'services/errorReportingService'
import type { AppDispatch } from 'src/client/store'
import type { ExperiencesPassportCheckoutSource } from 'components/Experiences/ExperiencesPassport/experiencesPassportTypes'

import { resolveSelectedOptionalBundles } from './propertyMultiRoomOptionalBundles'

function getFlightOverheadPrice(rate: App.EnrichedPropertyRate): number {
  if (typeof rate.cheapestFlightPrice === 'number') return rate.cheapestFlightPrice
  return 0
}

/**
 * The enquiry payload for a bedbank rate. Bedbank prices the hotel alone, so flights and bundled
 * experiences come back out of the totals first.
 *
 * TODO(HP-1329): Preserve original hotel-only prices so we needn't jump through these hoops.
 */
function buildBedbankEnquiry({ rate, rooms, checkIn, checkOut, regionCode, source, subscriber, isSpoofed, discount, gha }: {
  rate: App.EnrichedBedbankPropertyRate
  rooms: Array<App.Occupants>
  checkIn: string
  checkOut: string
  regionCode: string
  source: string
  subscriber: App.OrderSubscriber | undefined
  isSpoofed: boolean
  /** How much less than this rate's own price we're charging, for a loyalty package upgrade. */
  discount?: number
  gha?: GhaPriceMatchParams
}): CreateReservationEnquiryProps {
  const flightOverheadPrice = getFlightOverheadPrice(rate)
  // `baseSellingPrice`, not `sellingPrice`: svc-bedbank is unaware of channel markup and the price
  // sent to it is re-marked for the checkout display and again at order creation, so sending the
  // marked price marks the same rate twice. `rate.bundle` prices come straight off the server
  // un-marked (see `mapBundle`), so that branch is already a base price. Flights are not marked
  // either, so subtracting the flight overhead from the base is the same arithmetic as before.
  const [hotelSellingPrice, hotelLuxPlusSellingPrice] = rate.bundle ? [
    rate.bundle.hotelSellingPrice,
    rate.bundle.hotelLuxPlusSellingPrice || undefined,
  ] : [
    floatify(rate.totals.baseSellingPrice - flightOverheadPrice),
    rate.luxPlusTotals?.baseSellingPrice ? floatify(rate.luxPlusTotals.baseSellingPrice - flightOverheadPrice) : undefined,
  ]

  return {
    propertyId: rate.service.propertyId,
    roomTypeId: rate.service.roomId,
    roomRateId: rate.service.rateId,
    token: rate.service.token,
    rateGroupId: rate.service.rateGroupId,
    bedGroupId: rate.service.bedGroups[0].id,
    isFlightBundle: rate.isFlightBundle,
    csBooking: isSpoofed,
    region: regionCode,
    rooms,
    checkIn,
    checkOut,
    price: hotelSellingPrice,
    propertyFees: rate.totals.propertyFees,
    memberInclusive: hotelLuxPlusSellingPrice,
    subscriber,
    source,
    discount,
    gha,
  }
}

/**
 * Everything in a rate's total that svc-bedbank does not price: bundled experiences, or flights.
 *
 * Read off `baseSellingPrice`, so the result is an un-marked amount that can be added to the
 * un-marked hotel price svc-bedbank quotes. `bundle.hotelSellingPrice` is itself un-marked (see
 * `mapBundle`) and flights are never marked, so both branches stay on the base side.
 */
function getOverheadPrice(rate: App.EnrichedBedbankPropertyRate): number {
  // If bundled experiences are involved, we'll assume the overhead is consistent between
  // non-LuxPlus+ and LuxPlus+ pricing. Note that flights are already baked into `rate.totals`.
  if (rate.bundle) return floatify(rate.totals.baseSellingPrice - rate.bundle.hotelSellingPrice)
  // If bundled experiences are not involved, we just need to account for flights.
  return getFlightOverheadPrice(rate)
}

interface ChangedRate {
  rate: App.EnrichedBedbankPropertyRate
  newHotelSellingPrice?: number
  newHotelLuxPlusSellingPrice?: number
  newPropertyFees?: number
  rooms: Array<App.Occupants>
}

/**
 * Re-derive a rate's enriched totals from the hotel price svc-bedbank quoted.
 *
 * svc-bedbank prices the hotel alone and un-marked, so the bundled experiences come back in first.
 * Only that hotel-and-bundle portion is channel marked: flights are never marked, so the flight
 * price is added raw AFTER marking, mirroring the reducer and `enrichPropertyRooms`. Marking the
 * flight-inclusive total instead makes an unchanged flight rate read as a price rise.
 */
function resolveRepricedSellingPrices(
  rate: App.EnrichedBedbankPropertyRate,
  newHotelSellingPrice: number,
  channelMarkup: App.ChannelMarkupDetails | undefined,
): { baseSellingPrice: number, sellingPrice: number } {
  const flightOverheadPrice = getFlightOverheadPrice(rate)
  const markedPortion = floatify(newHotelSellingPrice + getOverheadPrice(rate) - flightOverheadPrice)
  return {
    baseSellingPrice: floatify(markedPortion + flightOverheadPrice),
    sellingPrice: floatify(markPropertyRateSellingPrice(markedPortion, channelMarkup) + flightOverheadPrice),
  }
}

function calculatePriceChange(ch: ChangedRate, isLuxPlusBooking: boolean): {
  oldDisplayPrice: number
  newDisplayPrice: number
  oldPropertyFees: number
  newPropertyFees: number
} {
  const oldDisplayPrice = (isLuxPlusBooking && !!ch.rate.luxPlusTotals)
    ? ch.rate.luxPlusTotals.sellingPrice
    : ch.rate.totals.sellingPrice

  // The new prices that we get back from bedbank service are just for the hotel, so we have to
  // factor flights and bundled experiences back in. They are also un-marked, so they have to be
  // marked before they can be compared against the marked price above or shown to the customer —
  // otherwise an unchanged rate reads as a price drop the size of the markup.
  const newLuxPlusPrice = ch.newHotelLuxPlusSellingPrice
    ? resolveRepricedSellingPrices(ch.rate, ch.newHotelLuxPlusSellingPrice, ch.rate.luxPlusTotals?.channelMarkup).sellingPrice
    : (ch.rate.luxPlusTotals?.sellingPrice ?? 0)
  const newDisplayPrice = (isLuxPlusBooking && newLuxPlusPrice)
    ? newLuxPlusPrice
    : ch.newHotelSellingPrice
      ? resolveRepricedSellingPrices(ch.rate, ch.newHotelSellingPrice, ch.rate.totals.channelMarkup).sellingPrice
      : ch.rate.totals.sellingPrice

  // NB: We're going to _assume_ the property fees are equivalent between standard and LuxPlus+
  // pricing.
  const oldPropertyFees = ch.rate.totals.propertyFees
  const newPropertyFees = ch.newPropertyFees ?? oldPropertyFees

  return {
    oldDisplayPrice,
    newDisplayPrice,
    oldPropertyFees,
    newPropertyFees,
  }
}

function calculateUpgradeUnavailablePriceChange(
  upgrade: App.Checkout.PropertyRateLoyaltyUpgrade,
  targetRate: App.EnrichedPropertyRate,
  isLuxPlusSubscriber: boolean,
  changedTargetRate?: ChangedRate,
): {
  oldDisplayPrice: number
  newDisplayPrice: number
  oldPropertyFees: number
  newPropertyFees: number
} {
  const changedTargetPrice = changedTargetRate
    ? calculatePriceChange(changedTargetRate, isLuxPlusSubscriber)
    : undefined
  const targetPricing = {
    totals: targetRate.totals,
    luxPlusTotals: targetRate.luxPlusTotals,
  }

  return {
    oldDisplayPrice: getPropertyRatePricingSellingPrice(upgrade.sourcePricing, isLuxPlusSubscriber),
    newDisplayPrice: changedTargetPrice?.newDisplayPrice ??
      getPropertyRatePricingSellingPrice(targetPricing, isLuxPlusSubscriber),
    oldPropertyFees: upgrade.sourcePricing.totals.propertyFees,
    newPropertyFees: changedTargetPrice?.newPropertyFees ?? targetRate.totals.propertyFees,
  }
}

/**
 * Dispatches an action to patch the changed rate in the offer store and returns a semi-patched rate
 * that can be used to perform a new bedbank service enquiry request.
 */
function updateChangedRate(dispatch: AppDispatch, {
  propertyId,
  checkIn,
  checkOut,
  utmContent,
  roomTypeId,
  roomRateId,
  changedRate: { rate, newHotelSellingPrice, newHotelLuxPlusSellingPrice, newPropertyFees, rooms },
}: {
  propertyId: string
  checkIn: string
  checkOut: string
  utmContent: string | undefined
  roomTypeId: string | undefined
  roomRateId: string | undefined
  changedRate: ChangedRate
}): App.EnrichedBedbankPropertyRate {
  // Patching occurs on `App.RawPropertyRate`s (and not `App.EnrichedPropertyRate`s), so we must
  // _exclude_ flights but _include_ bundled experiences.
  //
  // svc-bedbank's new price is un-marked, so it resolves the BASE totals; the marked totals are
  // then re-derived from it. Falling back keeps each side on its own price basis: reusing the
  // marked price as the new base is what marked the rate twice, and it is the base that a retried
  // enquiry sends.
  const repriced = newHotelSellingPrice
    ? resolveRepricedSellingPrices(rate, newHotelSellingPrice, rate.totals.channelMarkup)
    : { baseSellingPrice: rate.totals.baseSellingPrice, sellingPrice: rate.totals.sellingPrice }
  const repricedLuxPlus = newHotelLuxPlusSellingPrice
    ? resolveRepricedSellingPrices(rate, newHotelLuxPlusSellingPrice, rate.luxPlusTotals?.channelMarkup)
    : rate.luxPlusTotals && { baseSellingPrice: rate.luxPlusTotals.baseSellingPrice, sellingPrice: rate.luxPlusTotals.sellingPrice }
  const flightOverheadPrice = getFlightOverheadPrice(rate)
  // The reducer patches raw (un-marked) rates and re-derives the marked prices itself, so it is
  // sent the base price with flights taken back out.
  const resolvedRawBaseSellingPrice = floatify(repriced.baseSellingPrice - flightOverheadPrice)
  const resolvedRawLuxPlusBaseSellingPrice = repricedLuxPlus
    ? floatify(repricedLuxPlus.baseSellingPrice - flightOverheadPrice)
    : undefined

  // NB: We're going to _assume_ the property fees are equivalent between standard and LuxPlus+
  // pricing.
  const resolvedPropertyFees = newPropertyFees ?? rate.totals.propertyFees

  dispatch(updatePropertyPricingData(propertyId, {
    checkIn,
    checkOut,
    occupancies: rooms,
    utmContent,
    roomTypeId,
    roomRateId,
    roomId: rate.roomId,
    rateId: rate.rateId,
    newBasePrice: resolvedRawBaseSellingPrice,
    newLuxPlusBasePrice: resolvedRawLuxPlusBaseSellingPrice,
    newPropertyFees: resolvedPropertyFees,
  }))

  // We don't need to be as thorough here; we only need to patch the fields that're used to perform
  // a bedbank service enquiry request.
  return {
    ...rate,
    ...(rate.bundle && {
      bundle: {
        ...rate.bundle,
        ...(newHotelSellingPrice != null && {
          hotelSellingPrice: newHotelSellingPrice,
        }),
        ...(newHotelLuxPlusSellingPrice != null && {
          hotelLuxPlusSellingPrice: newHotelLuxPlusSellingPrice,
        }),
      },
    }),
    totals: {
      ...rate.totals,
      // Keeping `baseSellingPrice` in step matters: it is what a retried enquiry sends, and a stale
      // value would re-send the price svc-bedbank just rejected, so the booking could never
      // complete.
      ...repriced,
      propertyFees: resolvedPropertyFees,
    },
    ...(repricedLuxPlus?.sellingPrice && {
      luxPlusTotals: {
        ...(rate.luxPlusTotals ?? rate.totals),
        ...repricedLuxPlus,
        propertyFees: resolvedPropertyFees,
      },
    }),
  }
}

/**
 * GHA-404: the raw (un-marked) hotel price of the same rate at the public source, once the marker
 * has switched the selectors. Matched by room and option id: the rate id itself is ephemeral and
 * changes on every fetch. The page re-renders with public rates either way.
 */
function fetchPublicPropertyHotelPrice(dispatch: AppDispatch, {
  propertyId,
  rate,
  rooms,
  checkIn,
  checkOut,
  utmContent,
  roomOption,
}: {
  propertyId: string
  rate: App.EnrichedBedbankPropertyRate
  rooms: Array<App.Occupants>
  checkIn: string
  checkOut: string
  utmContent: string | undefined
  roomOption: OfferPageRoomOption | undefined
}): Promise<{ hotelSellingPrice: number, hotelLuxPlusSellingPrice: number | undefined, propertyFees: number } | undefined> {
  return new Promise((resolve) => {
    dispatch(fetchPropertyRates(propertyId, {
      checkIn,
      checkOut,
      occupancies: rooms,
      utmContent,
      roomTypeId: roomOption?.roomTypeId,
      roomRateId: roomOption?.roomRateId,
      roomRateGroupId: roomOption?.roomRateGroupId,
      refresh: true,
      onComplete: (result) => {
        if (result.status !== 'success') {
          resolve(undefined)
          return
        }
        for (const slot of Object.values(result.data)) {
          const room = slot.rooms?.find(candidate => candidate.roomId === rate.roomId)
          if (!room || !('rates' in room)) continue
          const publicRate = Object.values(room.rates).find(candidate => candidate.optionId === rate.optionId) ??
            Object.values(room.rates).find(candidate => candidate.groupKey === rate.groupKey)
          if (!publicRate) continue
          resolve({
            hotelSellingPrice: publicRate.bundle?.hotelSellingPrice ?? publicRate.totals.baseSellingPrice,
            hotelLuxPlusSellingPrice: publicRate.bundle?.hotelLuxPlusSellingPrice || publicRate.luxPlusTotals?.baseSellingPrice,
            propertyFees: publicRate.totals.propertyFees,
          })
          return
        }
        resolve(undefined)
      },
    }))
  })
}

export async function createBundledCheckoutItem(
  accommodationItemIds: Array<string>,
  bundleId: string,
  bundleOptin: boolean | undefined,
  ticket: { experienceId: string; productId: string; price: number },
  stay: { checkIn: string; checkOut: string },
  rooms: Array<App.Occupants>,
  property: App.Property.Property,
  context: {
    currentCurrency: string
    airports?: Array<App.Airport>
    currentRegionCode: string
    memberId?: string
  },
): Promise<App.Checkout.ExperienceItem | App.Checkout.TransferItem> {
  const fallback = () => generateBundledAccommodationExperienceItem(accommodationItemIds, bundleId, ticket, undefined, bundleOptin)

  try {
    const { currentCurrency, airports, currentRegionCode, memberId } = context
    const customerId = memberId

    const { experiences } = await getExperiencesById(
      [ticket.experienceId],
      { currentCurrency, currentRegionCode, airports },
    )
    const experience = experiences[0]

    if (experience?.productType !== 'transfer') {
      if (experience?.bookingType === 'CALENDAR-TIMESLOTS' || experience?.bookingType === 'CALENDAR-NO-TIMESLOTS') {
        return { ...fallback(), isBuyNowBookLater: true, bookingType: experience.bookingType }
      }
      return fallback()
    }

    const transferOptions = await getExperienceTransferAvailability(
      ticket.experienceId,
      EXPERIENCE_ANY_DATE,
      { currencyCode: currentCurrency, customerId },
    )
    const option = transferOptions.find(o => o.id === ticket.productId)
    const direction = option?.transferTypes[0]
    if (!option || !(direction === 'HOTEL-TO-AIRPORT' || direction === 'AIRPORT-TO-HOTEL')) {
      return fallback()
    }

    const occupancy = countOccupants(rooms)
    // svc-order books the pickup on whatever day the client sends, so the direction has to pick
    // the date here: a departure transfer leaves on check-out day, not arrival day.
    const date = direction === 'HOTEL-TO-AIRPORT' ? stay.checkOut : stay.checkIn
    return generateBundledAccommodationExperienceItem(accommodationItemIds, bundleId, ticket, {
      experience,
      option,
      date,
      hotel: {
        name: property.name,
        latitude: property.location.latitude,
        longitude: property.location.longitude,
        kmFromAirport: (() => {
          const d = getDistanceBetweenPropertyAndAirport(property as unknown as App.OfferProperty, experience.airport)
          return Number.isFinite(d) ? d : undefined
        })(),
      },
      travellers: {
        adults: occupancy.adults,
        children: occupancy.children ?? 0,
      },
      baggage: { bags: 0, oversizedBags: 0 },
    }, bundleOptin)
  } catch(error) {
    reportClientError(error instanceof Error ? error : new Error('Failed to create bundled checkout item'), {
      bundleId,
      experienceId: ticket.experienceId,
      productId: ticket.productId,
    }).catch(() => undefined)
    return fallback()
  }
}

interface SingleRoomArgs {
  shouldAddToCart: boolean
  dispatch: AppDispatch
  currentCurrency: string
  airports?: Array<App.Airport>
  currentRegionCode: string
  memberId?: string
  offerPageDispatch: React.Dispatch<OfferPageDispatchAction>
  modalElementContext: ModalElementContextValue<never>
  showModal: ModalContextType
  intl: IntlShape
  property: App.Property.Property
  rate: App.EnrichedPropertyRate
  ratePosition: number
  originAirportCode: string | undefined
  checkIn: string
  checkOut: string
  duration: number
  rooms: Array<App.Occupants>
  regionCode: string
  source: string
  utmContent: string | undefined
  roomOption: OfferPageRoomOption | undefined
  isSpoofed: boolean
  isLoggedIn: boolean
  isAccommodationV2BookingsEnabled: boolean
  /** This booking passed the guest-checkout guard before any auth modal was shown. */
  isGuestCheckout?: boolean
  /** Property-scoped source, retained for the deferred GHA voucher mint at checkout. */
  voucherSource?: string
  /** Set when bedbank must price this property at the GHA advertised price. */
  ghaPriceMatch?: GhaPriceMatchClickThrough
  subscriber: App.OrderSubscriber | undefined
  roomListId: string
  roomPosition: number
  rateListId: string
  metasearchPriceId?: string
  metasearchAccountType?: string
  metasearchRuleId?: string
  /** Google's displayed total and its currency, from the landing URL (GHA-585). */
  metasearchAdvertisedPrice?: number
  metasearchAdvertisedCurrency?: string
  shouldApplyDiscount?: boolean
  /** The GHA discount the rate tile took off its price (GHA-404: a withheld voucher removes it). */
  ghaDiscountAmount?: number
  loyalty?: {
    sourceRate: App.EnrichedPropertyRate
    targetRate: App.EnrichedPropertyRate
    upgrade: App.Checkout.PropertyRateLoyaltyUpgrade
  }
  experiencesPassportEnabled?: boolean
  experiencesPassportCheckoutSources?: Array<ExperiencesPassportCheckoutSource>
  selectedOptionalBundleIds?: Array<string>
  isLuxPlusMember: boolean
  displayMemberPrice?: number
  displayNonMemberPrice?: number
  displayValue?: number
}

function getValidationErrorInfo(error: unknown): { status?: number, message?: string } {
  if (error && typeof error === 'object') {
    const status = 'status' in error && typeof error.status === 'number' ? error.status : undefined
    const message = 'message' in error && typeof error.message === 'string' ? error.message : undefined
    return { status, message }
  }

  return {}
}

function isBundleRateNotFoundError(error: unknown): boolean {
  const { status, message } = getValidationErrorInfo(error)
  if (status === 404) {
    return true
  }

  return !!message && /rate.*not.*found/i.test(message)
}

export async function validateBundledPropertyRateForCheckout({
  checkIn,
  checkOut,
  rooms,
  regionCode,
  source,
  rate,
}: {
  checkIn: string
  checkOut: string
  rooms: Array<App.Occupants>
  regionCode: string
  source: string
  rate: App.EnrichedPropertyRate
}): Promise<boolean> {
  const bundle = rate.bundle
  if (!bundle) return true

  const bundleValidationRateId = rate.groupKey || rate.service.rateId || rate.rateId
  try {
    return await validatePropertyRateBundle({
      checkIn,
      checkOut,
      occupancies: rooms,
      regionCode,
      currency: rate.currency,
      source,
      dynamicAvailability: true,
      propertyId: rate.propertyId,
      rateId: bundleValidationRateId,
      tickets: bundle.tickets.map(({ experienceId, productId, price }) => ({
        experienceId,
        productId,
        ticketPrice: price,
      })),
      bundleId: bundle.bundleId,
    })
  } catch(error) {
    if (isBundleRateNotFoundError(error)) {
      const { status, message } = getValidationErrorInfo(error)
      reportClientError(new Error('Bundle validation rate not found; attempting checkout anyway'), {
        status,
        message,
        propertyId: rate.propertyId,
        rateId: bundleValidationRateId,
        bundleId: bundle.bundleId,
        tickets: bundle.tickets,
      }).catch(() => undefined)
      return true
    }

    const { status, message } = getValidationErrorInfo(error)
    reportClientError(error instanceof Error ? error : new Error('Bundle validation failed'), {
      status,
      message,
      propertyId: rate.propertyId,
      rateId: bundleValidationRateId,
      bundleId: bundle.bundleId,
      tickets: bundle.tickets,
    }).catch(() => undefined)
    return false
  }
}

/**
 * Validates the opt-in bundles the customer selected. Kept separate from
 * {@link validateBundledPropertyRateForCheckout} because accommodation service rejects (400) a
 * request carrying both a mandatory `bundleId`/`tickets` pair and `optionalBundles`; a rate with
 * both therefore issues two independent validation requests.
 */
export async function validateOptInBundlesForCheckout({
  checkIn,
  checkOut,
  rooms,
  regionCode,
  source,
  rate,
  optionalBundles,
}: {
  checkIn: string
  checkOut: string
  rooms: Array<App.Occupants>
  regionCode: string
  source: string
  rate: App.EnrichedPropertyRate
  optionalBundles: Array<App.Property.OptionalBundle>
}): Promise<boolean> {
  if (!optionalBundles.length) return true

  const requestOptionalBundles = optionalBundles.map(({ bundleId, tickets }) => ({
    bundleId,
    tickets: tickets.map(({ experienceId, productId, price }) => ({
      experienceId,
      productId,
      ticketPrice: price,
    })),
  }))

  const bundleValidationRateId = rate.groupKey || rate.service.rateId || rate.rateId
  try {
    return await validatePropertyRateBundle({
      checkIn,
      checkOut,
      occupancies: rooms,
      regionCode,
      currency: rate.currency,
      source,
      dynamicAvailability: true,
      propertyId: rate.propertyId,
      rateId: bundleValidationRateId,
      optionalBundles: requestOptionalBundles,
    })
  } catch(error) {
    const { status, message } = getValidationErrorInfo(error)
    if (isBundleRateNotFoundError(error)) {
      reportClientError(new Error('Opt-in bundle validation rate not found; attempting checkout anyway'), {
        status,
        message,
        propertyId: rate.propertyId,
        rateId: bundleValidationRateId,
        optionalBundles: requestOptionalBundles,
      }).catch(() => undefined)
      return true
    }

    reportClientError(error instanceof Error ? error : new Error('Opt-in bundle validation failed'), {
      status,
      message,
      propertyId: rate.propertyId,
      rateId: bundleValidationRateId,
      optionalBundles: requestOptionalBundles,
    }).catch(() => undefined)
    return false
  }
}

export async function propertySingleRoomBookNowOrAddToCartCallback(args: SingleRoomArgs): Promise<void> {
  const {
    shouldAddToCart,
    dispatch,
    currentCurrency,
    airports,
    currentRegionCode,
    memberId,
    offerPageDispatch,
    modalElementContext,
    showModal,
    intl,
    property,
    rate,
    ratePosition,
    originAirportCode,
    checkIn,
    checkOut,
    duration,
    rooms,
    regionCode,
    source,
    utmContent,
    roomOption,
    isSpoofed,
    isLoggedIn,
    isAccommodationV2BookingsEnabled,
    isGuestCheckout = false,
    voucherSource,
    ghaPriceMatch,
    subscriber,
    roomListId,
    roomPosition,
    rateListId,
    metasearchPriceId,
    metasearchAccountType,
    metasearchRuleId,
    metasearchAdvertisedPrice,
    metasearchAdvertisedCurrency,
    shouldApplyDiscount,
    ghaDiscountAmount = 0,
    loyalty,
    selectedOptionalBundleIds = [],
    isLuxPlusMember,
    displayMemberPrice,
    displayNonMemberPrice,
    displayValue,
  } = args
  const period = { checkIn, checkOut, duration }
  let sessionId: string | undefined
  const accommodationItems: Array<
    | App.Checkout.InstantBookingLEHotelItem
    | App.Checkout.BedbankHotelItem
    | App.Checkout.AccommodationV2Item
  > = []
  const bundle = rate.bundle
  const useAccommodationV2 = isAccommodationV2Booking({
    property,
    isLoggedIn,
    isAccommodationV2BookingsEnabled,
    source,
    utmContent,
  })
  const selectedOptionalBundles = resolveSelectedOptionalBundles(rate, selectedOptionalBundleIds)

  // Show the loading modal immediately when bundle data is needed, and keep it open until all bundled
  // checkout items have been created.
  let closeProcessingModal: (() => void) | undefined
  const closeOpenProcessingModal = () => {
    closeProcessingModal?.()
    closeProcessingModal = undefined
  }
  const openProcessingModal = () => {
    if (closeProcessingModal) return
    showModal((resolve) => {
      closeProcessingModal = resolve as () => void
      return <ProcessingModal message={intl.formatMessage({
        id: '65JLQI9bUAc3Orze8tm-',
        defaultMessage: 'Please wait...',
        description: 'Processing modal message when initializing bedbank session',
      })}
      />
    })
  }

  if (bundle || selectedOptionalBundles.length) {
    openProcessingModal()
  }

  const refreshPropertyRates = () => {
    dispatch(fetchPropertyRates(property.id, {
      checkIn,
      checkOut,
      occupancies: rooms,
      utmContent,
      roomTypeId: roomOption?.roomTypeId,
      roomRateId: roomOption?.roomRateId,
      roomRateGroupId: roomOption?.roomRateGroupId,
      refresh: true,
    }))
  }
  const refreshRatesAndShowSoldOut = () => {
    refreshPropertyRates()
    showModal(<BedbankSoldOutModal />)
  }

  if (!await validateBundledPropertyRateForCheckout({
    checkIn,
    checkOut,
    regionCode,
    source,
    rooms,
    rate,
  })) {
    closeOpenProcessingModal()
    refreshRatesAndShowSoldOut()
    return
  }

  if (!await validateOptInBundlesForCheckout({
    checkIn,
    checkOut,
    regionCode,
    source,
    rooms,
    rate,
    optionalBundles: selectedOptionalBundles,
  })) {
    closeOpenProcessingModal()
    refreshRatesAndShowSoldOut()
    return
  }

  // Eager dual-session: the side the member is *not* booking — the source when the upgrade is applied,
  // the target when it is only available — is what the checkout toggle switches to. If that side is a
  // bedbank rate, enquire it up front so the toggle needs no fresh enquiry.
  //
  // Started rather than awaited. It is independent of the booked rate's own enquiry below — a
  // different rate, producing a different session — and its result is not needed until the checkout
  // items are built, so awaiting it here would serialise two round-trips that can overlap.
  const loyaltyUpgrade = loyalty?.upgrade
  const isUpgradeApplied = loyaltyUpgrade?.applied !== false
  const upgradeOtherRate = isUpgradeApplied ? loyalty?.sourceRate : loyalty?.targetRate
  let hasRejectedLoyaltyUpgrade = false
  const rejectLoyaltyUpgrade = () => {
    if (!loyalty || hasRejectedLoyaltyUpgrade) return
    hasRejectedLoyaltyUpgrade = true
    recordRejectedPropertyRateUpgrade({
      propertyId: property.id,
      sourceRateId: loyalty.sourceRate.service.rateId,
      targetRateId: loyalty.targetRate.service.rateId,
      checkIn,
      checkOut,
      occupancy: rooms[0],
    })
    // The rejection store is outside Redux, so refresh the selector input before this mounted offer
    // page can advertise the same pair again.
    refreshPropertyRates()
  }
  const continueWithoutAppliedUpgrade = async(
    reason: 'property-fees' | 'upgrade-margin',
    changedTargetRate?: ChangedRate,
  ): Promise<void> => {
    if (!loyaltyUpgrade || !loyalty) return
    closeOpenProcessingModal()
    rejectLoyaltyUpgrade()
    const accepted = await showModal<boolean | undefined>(<PropertyLoyaltyUpgradeUnavailableModal
      reason={reason}
      {...calculateUpgradeUnavailablePriceChange(
        loyaltyUpgrade,
        loyalty.targetRate,
        !!subscriber?.tier,
        changedTargetRate,
      )}
    />)
    if (!accepted) return

    const nextRate = changedTargetRate ? updateChangedRate(dispatch, {
      propertyId: property.id,
      checkIn,
      checkOut,
      utmContent,
      roomTypeId: roomOption?.roomTypeId,
      roomRateId: roomOption?.roomRateId,
      changedRate: changedTargetRate,
    }) : rate
    return propertySingleRoomBookNowOrAddToCartCallback({
      ...args,
      rate: nextRate,
      loyalty: undefined,
      intl,
    })
  }
  // Only the loyalty package upgrade's target enquiry carries the discount because it books the
  // target rate at the source rate's price.
  const upgradeDiscount = loyaltyUpgrade && getUpgradePricingDiscount(loyaltyUpgrade, !!subscriber?.tier)
  // A rate can arrive without bed groups, which the enquiry payload requires. Skip only that rate's
  // toggle-side prefetch; the booked rate remains valid and checkout can enquire the other side later.
  const canPrefetchToggleSide = !!upgradeOtherRate && isBedbankRate(upgradeOtherRate) && !!upgradeOtherRate.service.bedGroups?.[0]
  // AccomV2 needs no prefetch: its checkout toggle mints its own session, and svc-order costs the
  // upgrade's source from availability.
  const toggleSessionPromise = !useAccommodationV2 && loyaltyUpgrade && upgradeOtherRate && canPrefetchToggleSide && !shouldAddToCart
    ? initBedbankSession(dispatch, buildBedbankEnquiry({
        rate: upgradeOtherRate,
        rooms,
        checkIn,
        checkOut,
        regionCode,
        source,
        subscriber,
        isSpoofed,
        discount: isUpgradeApplied ? undefined : upgradeDiscount,
      }))
        // Best-effort — the user is booking this side, so a failed enquiry just defers the toggle.
        // Caught as the promise is created, so abandoning it on an early return is always safe.
        .catch(() => undefined)
    : undefined

  /**
   * Await the toggle-side enquiry only while building checkout items, then attach its session to the
   * upgrade. This keeps the toggle-side and booked-rate enquiries concurrent.
   */
  const resolveUpgradeWithSessions = async(): Promise<{
    upgrade: App.Checkout.PropertyRateLoyaltyUpgrade | undefined
    feeMismatch: boolean
  }> => {
    const toggleSession = await toggleSessionPromise
    if (!loyaltyUpgrade || !toggleSession) return { upgrade: loyaltyUpgrade, feeMismatch: false }
    if (!('sessionId' in toggleSession)) {
      const feeMismatch = hasPropertyRateUpgradeFeeMismatchBetweenRates(
        toggleSession.propertyFees,
        rate.totals.propertyFees,
      )
      if (feeMismatch) rejectLoyaltyUpgrade()
      return { upgrade: feeMismatch ? undefined : loyaltyUpgrade, feeMismatch }
    }
    return {
      upgrade: withPropertyRateUpgradeSessions(loyaltyUpgrade, isUpgradeApplied ? {
        sourceSessionId: toggleSession.sessionId,
      } : {
        targetSessionId: toggleSession.sessionId,
      }),
      feeMismatch: false,
    }
  }

  if (useAccommodationV2) {
    // AccomV2 sessions price the hotel alone, so the upgrade does too. Both sides' rate fields are kept
    // even with opt-in bundles, which hide the checkout toggle, so a multi-cart checkout can still
    // restore the source rate as the legacy flow does.
    const accommodationV2Upgrade = loyaltyUpgrade && loyalty ? {
      ...loyaltyUpgrade,
      sourcePricing: getHotelOnlyPropertyRatePricing(loyalty.sourceRate),
      targetPricing: getHotelOnlyPropertyRatePricing(loyalty.targetRate),
      sourceAccommodationV2Rate: getAccommodationV2RateFields(loyalty.sourceRate),
      targetAccommodationV2Rate: getAccommodationV2RateFields(loyalty.targetRate),
    } : loyaltyUpgrade
    let sessionIds: Array<string> | undefined
    if (!shouldAddToCart) {
      // Reuse the bundle modal if already open; otherwise open one for session create.
      if (!closeProcessingModal) {
        openProcessingModal()
      }
      try {
        sessionIds = await initAccommodationV2Sessions(dispatch, {
          selections: rooms.map(occupancy => ({
            rate,
            occupancy,
            ...(selectedOptionalBundles.length > 0 && { selectedOptionalBundles }),
            ...(accommodationV2Upgrade && { loyaltyUpgrade: accommodationV2Upgrade }),
          })),
          checkIn,
          checkOut,
          region: regionCode,
          ...(!!subscriber?.tier && { isLuxPlusUser: true }),
        })
        // Bundled experience items still need to be built below; keep the modal for that.
        if (!bundle && !selectedOptionalBundles.length) {
          closeOpenProcessingModal()
        }
      } catch {
        closeOpenProcessingModal()
        refreshRatesAndShowSoldOut()
        return
      }
    }

    accommodationItems.push(...rooms.map((occupancy, index) =>
      generateAccommodationV2CheckoutItem(
        rate,
        period,
        occupancy,
        sessionIds?.[index],
        accommodationV2Upgrade,
        property.airportCode,
        selectedOptionalBundles,
      ),
    ))
  } else {
  // Either enquiry is a supplier round-trip, so both need a loading state. An LPC rate whose upgrade
  // pairs with a bedbank one takes the prefetch alone, and used to wait with nothing on screen.
    if (toggleSessionPromise || (!shouldAddToCart && isBedbankRate(rate))) {
      openProcessingModal()
    }

    if (isLpcRate(rate)) {
      const resolvedUpgrade = await resolveUpgradeWithSessions()
      if (resolvedUpgrade.feeMismatch && isUpgradeApplied) {
        return continueWithoutAppliedUpgrade('property-fees')
      }
      accommodationItems.push(...generateLpcPropertyAccommodationCheckoutItems(
        rate,
        period,
        rooms,
        resolvedUpgrade.upgrade,
        selectedOptionalBundles,
      ))
    } else if (isBedbankRate(rate)) {
      if (!shouldAddToCart) {
        try {
          const priced = ghaPricedRequest(source, ghaPriceMatch)
          const enquiryPayload = buildBedbankEnquiry({
            rate,
            rooms,
            checkIn,
            checkOut,
            regionCode,
            source: priced.source,
            gha: priced.gha,
            subscriber,
            isSpoofed,
            discount: isUpgradeApplied ? upgradeDiscount : undefined,
          })
          const session = await initBedbankSession(dispatch, enquiryPayload)
          if (!('sessionId' in session)) {
            closeOpenProcessingModal()
            const changedRate: ChangedRate = {
              rate,
              newHotelSellingPrice: session.price,
              newHotelLuxPlusSellingPrice: session.luxplusPrice,
              newPropertyFees: session.propertyFees,
              rooms,
            }
            const hasUpgradeFeeMismatch = !!loyaltyUpgrade && !!upgradeOtherRate &&
              hasPropertyRateUpgradeFeeMismatchBetweenRates(
                session.propertyFees,
                upgradeOtherRate.totals.propertyFees,
              )
            if (hasUpgradeFeeMismatch && isUpgradeApplied) {
              return continueWithoutAppliedUpgrade('property-fees', changedRate)
            }
            const upgradeFailureCause = resolveUpgradeFailure({
              type: 'price-change',
              price: session.price,
              luxplusPrice: session.luxplusPrice,
              validWithoutDiscount: session.validWithoutDiscount,
            }, {
              hasUpgradeApplied: !!loyaltyUpgrade && isUpgradeApplied,
              nonDiscountedPrice: (!!subscriber?.tier && enquiryPayload.memberInclusive) || enquiryPayload.price,
              isLuxPlusSubscriber: !!subscriber?.tier,
            })
            if (upgradeFailureCause === 'upgrade-margin') {
              return continueWithoutAppliedUpgrade('upgrade-margin', changedRate)
            }
            if (hasUpgradeFeeMismatch) rejectLoyaltyUpgrade()
            const hasUserAcceptedNewRates = await showModal<boolean | undefined>(<BedbankChangePriceModal
              {...calculatePriceChange(changedRate, !!subscriber?.tier)}
            />)
            if (hasUserAcceptedNewRates) {
              const newRate = updateChangedRate(dispatch, {
                propertyId: property.id,
                checkIn,
                checkOut,
                utmContent,
                roomTypeId: roomOption?.roomTypeId,
                roomRateId: roomOption?.roomRateId,
                changedRate,
              })
              return propertySingleRoomBookNowOrAddToCartCallback({
                ...args,
                rate: newRate,
                ...(hasUpgradeFeeMismatch && { loyalty: undefined }),
                intl,
              })
            }
            // Even if the user doesn't accept the new rates, there's no point displaying the stale rates.
            if (!hasUpgradeFeeMismatch) refreshPropertyRates()
            return
          }
          sessionId = session.sessionId
        } catch {
          closeOpenProcessingModal()
          // We've hit an unrecoverable error and expect at least one of the now-stale rates to be
          // blocklisted, so let's refresh.
          dispatch(fetchPropertyRates(property.id, {
            checkIn,
            checkOut,
            occupancies: rooms,
            utmContent,
            roomTypeId: roomOption?.roomTypeId,
            roomRateId: roomOption?.roomRateId,
            roomRateGroupId: roomOption?.roomRateGroupId,
            refresh: true,
          }))
          showModal(<BedbankSoldOutModal />)
          return
        }
      }

      // Capture the booked bedbank rate's enquiry session on the upgrade (its target side when applied,
      // its source side when only available) so the checkout toggle can re-use it.
      const resolvedUpgrade = await resolveUpgradeWithSessions()
      if (resolvedUpgrade.feeMismatch && isUpgradeApplied) {
        return continueWithoutAppliedUpgrade('property-fees')
      }
      const bedbankUpgrade = resolvedUpgrade.upgrade
        ? withPropertyRateUpgradeSessions(resolvedUpgrade.upgrade, isUpgradeApplied ? {
            targetSessionId: sessionId,
          } : {
            sourceSessionId: sessionId,
          })
        : undefined
      accommodationItems.push(...generateBedbankPropertyAccommodationCheckoutItems(
        sessionId,
        rate,
        period,
        rooms,
        bedbankUpgrade,
        property.airportCode,
        selectedOptionalBundles,
        // Google's price is for Book now only, the one path that checks for a flagged shopper.
        shouldAddToCart ? undefined : ghaPriceMatch,
      ))
    }
  }

  const flightItems: Array<App.Checkout.FlightItem> = []
  if (rate.includesFlights) {
    if (!(originAirportCode && typeof rate.cheapestFlightPrice === 'number')) {
      closeOpenProcessingModal()
      throw new Error('Attempted to perform cart action on rate with flights when no flight pricing is available')
    }
    const flightOccupants = countOccupantsForFlights(rooms)
    flightItems.push(generateFlightCheckoutItem({
      originAirportCode,
      destinationAirportCode: property.airportCode,
      occupants: flightOccupants,
      passengers: generatePassengersFromOccupants(flightOccupants),
      fareType: 'return',
      viewType: FlightViewTypes.TWO_ONE_WAYS,
      bundledItemIds: accommodationItems.map(({ itemId }) => itemId),
      quotedFare: rate.cheapestFlightPrice,
    }))
  }

  const bundledExperienceItems: Array<App.Checkout.ExperienceItem | App.Checkout.TransferItem> = []
  if (bundle || selectedOptionalBundles.length) {
    const accommodationItemIds = accommodationItems.map(({ itemId }) => itemId)
    const bundledTickets: Array<{ bundleId: string, bundleOptin?: boolean, ticket: { experienceId: string; productId: string; price: number } }> = [
      ...(bundle?.tickets ?? []).map(ticket => ({ bundleId: bundle!.bundleId, ticket })),
      ...selectedOptionalBundles.flatMap(optionalBundle => optionalBundle.tickets.map(({ experienceId, productId, price }) => ({
        bundleId: optionalBundle.bundleId,
        bundleOptin: true,
        ticket: { experienceId, productId, price },
      }))),
    ]
    bundledExperienceItems.push(...await Promise.all(bundledTickets.map(({ bundleId, bundleOptin, ticket }) =>
      createBundledCheckoutItem(accommodationItemIds, bundleId, bundleOptin, ticket, { checkIn, checkOut }, rooms, property, {
        currentCurrency,
        airports,
        currentRegionCode,
        memberId,
      }),
    )))
  }

  closeOpenProcessingModal()

  const checkoutItems: Array<App.Checkout.AnyItem> = [...accommodationItems, ...flightItems, ...bundledExperienceItems]

  offerPageDispatch({ type: OfferPageActions.closeFilter })
  modalElementContext?.resolve()

  if (flightItems.length) {
    dispatch(initialiseCheckout(checkoutItems, { mode: 'vertical-select' }))
  } else if (!shouldAddToCart) {
    dispatch(addToCheckoutCart(checkoutItems))
  }

  fireEcommerceGTMEvent(addPropertyRateToCartGTM(property, rate, sumUpOccupancies(rooms)))
  // TODO(Xiao): Figure out why the other rate types don't fire this event for flights.
  trackEvent(addToCart({
    roomListId,
    roomPosition,
    rateListId,
    ratePosition,
    type: 'property',
    rate,
    occupancy: rooms,
    checkIn,
    checkOut,
    isLuxPlusMember,
    displayMemberPrice,
    displayNonMemberPrice,
    displayValue,
    staysOnPage: shouldAddToCart && !flightItems.length,
  }))

  if (flightItems.length) {
    dispatch(navigateToCheckout({ skipSpoofedModal: true }))
    return
  }

  if (shouldAddToCart) {
    dispatch(addToUserCart(checkoutItems))
    return
  }

  // TODO(GHA-661): price-matched rooms still mint so svc-connection-google can flag the shopper
  // (GHA-404). Once Hotel Sight has its own endpoint, go back to `&& !ghaPriceMatch`.
  if ((shouldApplyDiscount || ghaPriceMatch) && isBedbankRate(rate) && !rate.includesFlights) {
    const voucherInput = {
      token: metasearchPriceId,
      accountType: metasearchAccountType,
      propertyId: rate.service.propertyId,
      checkIn,
      checkOut,
      rooms,
      mx: rate.totals.mx,
      rateRuleId: metasearchRuleId,
      source: voucherSource,
      advertisedPrice: metasearchAdvertisedPrice,
      advertisedCurrency: metasearchAdvertisedCurrency,
    }
    if (isGuestCheckout) {
      setPendingGhaVoucher(voucherInput)
    } else {
      const outcome = await dispatch(createGhaVoucher(voucherInput))
      if (outcome === 'public_rate') {
        // GHA-404: the service flagged the shopper and withheld the voucher; the session is now
        // public-rate only. Show the public price if it differs, then book again: the retry
        // enquires with the public source and skips the mint. Never continue on this session,
        // which was enquired at the member rate.
        // Its booking is already in the cart: take it out, so the retry doesn't add a second copy
        // and Return to offer doesn't leave a booking the shopper refused.
        checkoutItems.forEach(item => dispatch(removeItem(item.itemId)))
        const publicPrice = await fetchPublicPropertyHotelPrice(dispatch, {
          propertyId: property.id,
          rate,
          rooms,
          checkIn,
          checkOut,
          utmContent,
          roomOption,
        })
        const changedRate: ChangedRate | undefined = publicPrice && {
          rate,
          newHotelSellingPrice: publicPrice.hotelSellingPrice,
          // A public rate without a LuxPlus price charges members the standard price. Left undefined,
          // the reprice helpers would keep the member-rate LuxPlus price this session has lost.
          newHotelLuxPlusSellingPrice: publicPrice.hotelLuxPlusSellingPrice ?? publicPrice.hotelSellingPrice,
          newPropertyFees: publicPrice.propertyFees,
          rooms,
        }
        if (changedRate) {
          const priceChange = calculatePriceChange(changedRate, !!subscriber?.tier)
          // The shopper saw the rate less the GHA discount, which the withheld voucher takes away.
          priceChange.oldDisplayPrice = Math.max(priceChange.oldDisplayPrice - ghaDiscountAmount, 0)
          const hasPriceChanged = priceChange.oldDisplayPrice !== priceChange.newDisplayPrice ||
            priceChange.oldPropertyFees !== priceChange.newPropertyFees
          if (hasPriceChanged) {
            const hasUserAcceptedPublicRate = await showModal<boolean | undefined>(<BedbankChangePriceModal {...priceChange} />)
            trackClientEvent({
              subject: 'gha_public_rate',
              action: hasUserAcceptedPublicRate ? 'accept' : 'return_to_offer',
              category: 'metasearch',
              type: 'interaction',
            })
            if (!hasUserAcceptedPublicRate) return
          }
        }
        const repricedRate = changedRate ? updateChangedRate(dispatch, {
          propertyId: property.id,
          checkIn,
          checkOut,
          utmContent,
          roomTypeId: roomOption?.roomTypeId,
          roomRateId: roomOption?.roomRateId,
          changedRate,
        }) : rate
        // The standard-price fallback above is for the modal only. Booked as the LuxPlus price, it
        // fails the enquiry: svc-bedbank rejects a LuxPlus price that isn't below the standard price.
        const retryRate = publicPrice && publicPrice.hotelLuxPlusSellingPrice === undefined ? {
          ...repricedRate,
          luxPlusTotals: undefined,
          bundle: repricedRate.bundle && { ...repricedRate.bundle, hotelLuxPlusSellingPrice: undefined },
        } : repricedRate
        // The caller's source and price match were resolved before the marker was set.
        const livePricing = dispatch((_dispatch, getState) => {
          const state = getState()
          return {
            source: `${getSource(state)},${SOURCE_TYPES.CTA}`,
            voucherSource: getSourceForProperty(state, rate.service.propertyId),
          }
        })
        return propertySingleRoomBookNowOrAddToCartCallback({
          ...args,
          rate: retryRate,
          source: livePricing.source,
          voucherSource: livePricing.voucherSource,
          ghaPriceMatch: undefined,
          intl,
        })
      }
    }
  }

  if (isGuestCheckout) {
    dispatch(setCheckoutGuestMode(true))
  }

  dispatch(navigateToCheckout())
}
